<script lang="ts">
    export enum TextInputWidth {
        Full = 'w-full flex-grow',
        ExtraLarge = 'w-lg',
        Large = 'w-80',
        Medium = 'w-64',
        Small = 'w-40',
        Tiny = 'w-20',
        Auto = '',
    }
</script>

<script setup lang="ts">
    import { computed, ref, useSlots, watch } from 'vue';
    import { useValidationErrors } from '@/js/composables/useValidationErrors';
    import ErrorMessage from './ErrorMessage.vue';

    const props = withDefaults(
        defineProps<{
            width?: TextInputWidth;
            disabled?: boolean;
            invalid?: boolean;
            validationKey?: string;
            scope?: string;
        }>(),
        {
            width: undefined,
            disabled: false,
            invalid: undefined,
            validationKey: undefined,
            scope: undefined,
        }
    );

    const model = defineModel<string | number>();
    const slots = useSlots();
    const inputRef = ref<HTMLInputElement | null>(null);

    const v = props.validationKey ? useValidationErrors(props.scope) : null;

    const effectiveInvalid = computed(() =>
        props.invalid !== undefined
            ? props.invalid
            : !!(v && props.validationKey && v.hasError(props.validationKey))
    );

    const effectiveErrorId = computed(() =>
        v && props.validationKey ? v.errorId(props.validationKey) : undefined
    );

    defineExpose({
        focus: () => inputRef.value?.focus(),
    });

    defineOptions({
        inheritAttrs: false,
    });

    watch(model, () => {
        if (props.validationKey) v?.clearField(props.validationKey);
    });

    const classes = computed(() => {
        return [
            'TextInput',
            props.width || TextInputWidth.Auto,
            { 'TextInput--invalid': effectiveInvalid.value },
        ];
    });
</script>
<template>
    <div class="block relative">
        <input
            ref="inputRef"
            v-model="model"
            type="text"
            v-bind="$attrs"
            :class="classes"
            :disabled="props.disabled"
            :aria-invalid="effectiveInvalid || undefined"
            :aria-describedby="effectiveErrorId"
        />
        <span v-if="slots['end-adornment']" class="TextInput__Icon">
            <slot name="end-adornment" />
        </span>
    </div>
    <ErrorMessage v-if="validationKey" :validation-key="validationKey" :scope="scope" />
</template>
